import get from 'lodash/get';
import { h, defineComponent, computed } from 'vue';
import { productMetaFieldTransformer } from '@/components/shop/products/Product';
import { addVariantToCart } from '@/utils/product';
import { useStore } from 'vuex';

export default defineComponent({
  name: 'ProductProvider',

  props: {
    handle: {
      type: String,
      required: true,
    },
  },

  setup(props) {
    const store = useStore();
    const { $shopifyProductByHandle, $productReviews } = useNuxtApp();

    // Fetch product data and reviews using useAsyncData
    const { data: productData } = useAsyncData(
      `product-${props.handle}`,
      async () => {
        // Skip request if handle is 'null'
        if (props.handle === 'null') {
          return { product: null, tsProduct: null };
        }

        try {
          // Fetch product data
          const productRequest = await $shopifyProductByHandle(props.handle);
          const product = productMetaFieldTransformer(productRequest);

          // Try to fetch reviews
          let tsProduct = null;
          try {
            const sku = product?.variants?.edges?.[0]?.node?.sku || null;
            if (sku) {
              tsProduct = await $productReviews.fetchReviews(sku);
            }
          } catch (error) {
            console.error('Error fetching reviews:', error);
          }

          return { product, tsProduct };
        } catch (error) {
          console.error('Error fetching product:', error);
          return { product: null, tsProduct: null, error };
        }
      },
      {
        // Cache product data to prevent unnecessary refetching
        watch: [() => props.handle],
      }
    );

    // Create computed properties for the component
    const product = computed(() => productData.value?.product || null);
    const tsProduct = computed(() => productData.value?.tsProduct || null);
    const error = computed(() => productData.value?.error || null);

    const firstVariant = computed(() => {
      return product.value?.variants?.edges[0]?.node;
    });

    const addToCartEnabled = computed(() => {
      return product.value?.availableForSale;
    });

    const addToCartDisabled = computed(() => {
      return !addToCartEnabled.value;
    });

    const price = computed(() => {
      return firstVariant.value?.price || null;
    });

    const compareAtPrice = computed(() => {
      return firstVariant.value?.compareAtPrice || null;
    });

    const priceRangeMin = computed(() => {
      return get(product.value, 'priceRange.minVariantPrice.amount', null);
    });

    const priceRangeMax = computed(() => {
      return get(product.value, 'priceRange.maxVariantPrice.amount', null);
    });

    const title = computed(() => {
      return product.value?.title;
    });

    const isCourse = computed(() => {
      return product.value?.digitalProductType?.digitalProductType === 'Online Course';
    });

    // Get line items from the cart store
    const lineItems = computed(() => {
      return store.getters['cart/lineItems'];
    });

    // Method to add product to cart
    const addToCart = () => {
      if (!addToCartEnabled.value) return;

      // Call the addVariantToCart utility with the correct context
      addVariantToCart.call(
        { $store: store, $dlPush: useNuxtApp().$dlPush, lineItems: lineItems.value },
        product.value,
        firstVariant.value,
        isCourse.value,
        1
      );
    };

    // Return everything needed for the render function
    return {
      product,
      error,
      addToCartEnabled,
      addToCartDisabled,
      addToCart,
      firstVariant,
      price,
      compareAtPrice,
      priceRangeMin,
      priceRangeMax,
      title,
      tsProduct,
      isCourse,
      lineItems,
    };
  },

  render() {
    // Prepare the slot props
    const slotProps = {
      product: this.product,
      error: this.error,
      addToCartEnabled: this.addToCartEnabled,
      addToCartDisabled: this.addToCartDisabled,
      addToCart: this.addToCart,
      firstVariant: this.firstVariant,
      price: this.price,
      compareAtPrice: this.compareAtPrice,
      priceRangeMin: this.priceRangeMin,
      priceRangeMax: this.priceRangeMax,
      title: this.title,
      tsProduct: this.tsProduct,
      isCourse: this.isCourse,
    };

    // Create the default slot with the props
    const slots = this.$slots.default?.(slotProps) || [];

    // Render the component
    return h('div', {}, slots);
  },
});
